const TEXT_KEYS = new Set(["#text", "#cdata"]);

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function formatCellValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean" || typeof value === "number") {
    return Number.isNaN(value) ? "" : value;
  }
  return String(value);
}

/**
 * Flatten parsed XML into column paths.
 * Root children: `root.child`. Nested: `parent.child`. Repeats: `parent.child`, `parent.child1`, ...
 */
export function flattenXmlObject(parsed) {
  const columns = {};

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return columns;
  }

  const rootKeys = Object.keys(parsed);
  if (rootKeys.length !== 1) {
    return columns;
  }

  const rootKey = rootKeys[0];
  const rootValue = parsed[rootKey];

  if (!isPlainObject(rootValue)) {
    columns[rootKey] = formatCellValue(rootValue);
    return columns;
  }

  flattenChildren(rootValue, rootKey, columns, true);
  return columns;
}

/**
 * Flatten parsed XML into one or more row objects.
 *
 * If the root has exactly one array child (e.g. <data><item/>...<item/></data>),
 * each array element becomes its own Excel row. Otherwise returns a single flattened row.
 */
export function flattenXmlToRows(parsed) {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return [{}];
  }

  const rootKeys = Object.keys(parsed);
  if (rootKeys.length !== 1) {
    return [{}];
  }

  const rootKey = rootKeys[0];
  const rootValue = parsed[rootKey];

  if (!isPlainObject(rootValue)) {
    return [{ [rootKey]: formatCellValue(rootValue) }];
  }

  const entries = Object.entries(rootValue);
  const arrayEntries = entries.filter(([, value]) => Array.isArray(value));
  const nonArrayEntries = entries.filter(([, value]) => !Array.isArray(value));

  if (arrayEntries.length === 1 && arrayEntries[0][1].length > 0) {
    const [arrayKey, arrayItems] = arrayEntries[0];
    const sharedColumns = {};

    // Keep non-array root siblings on every expanded row.
    for (const [key, value] of nonArrayEntries) {
      if (TEXT_KEYS.has(key)) {
        sharedColumns[rootKey] = formatCellValue(value);
        continue;
      }
      if (key.startsWith("@_")) {
        sharedColumns[`${rootKey}.${key}`] = formatCellValue(value);
        continue;
      }
      if (isPlainObject(value)) {
        flattenChildren(value, key, sharedColumns, false);
        continue;
      }
      sharedColumns[`${rootKey}.${key}`] = formatCellValue(value);
    }

    return arrayItems.map((item) => {
      const columns = { ...sharedColumns };
      writeLeaf(arrayKey, item, columns);
      return columns;
    });
  }

  return [flattenXmlObject(parsed)];
}

function flattenChildren(node, parentSegment, columns, isDirectRootChild) {
  for (const [key, value] of Object.entries(node)) {
    if (TEXT_KEYS.has(key)) {
      columns[parentSegment] = formatCellValue(value);
      continue;
    }

    if (key.startsWith("@_")) {
      columns[`${parentSegment}.${key}`] = formatCellValue(value);
      continue;
    }

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const base = `${parentSegment}.${key}`;
        const columnName = index === 0 ? base : `${base}${index}`;
        writeLeaf(columnName, item, columns);
      });
      continue;
    }

    if (isPlainObject(value)) {
      const nestedParent = isDirectRootChild ? key : key;
      flattenChildren(value, nestedParent, columns, false);
      continue;
    }

    columns[`${parentSegment}.${key}`] = formatCellValue(value);
  }
}

function writeLeaf(columnName, value, columns) {
  if (isPlainObject(value)) {
    const textEntry = Object.entries(value).find(([k]) => TEXT_KEYS.has(k));
    if (textEntry) {
      columns[columnName] = formatCellValue(textEntry[1]);
      return;
    }
    flattenChildren(value, columnName, columns, false);
    return;
  }

  columns[columnName] = formatCellValue(value);
}

/**
 * Merge flattened rows into a stable header list and row matrix.
 * Excel rejects undefined/null in some hosts, so values are normalized.
 */
export function buildOutputTable(flatRows) {
  const headers = [];
  const headerSet = new Set();

  flatRows.forEach((row) => {
    if (!row.columns) return;
    Object.keys(row.columns).forEach((header) => {
      if (!headerSet.has(header)) {
        headerSet.add(header);
        headers.push(header);
      }
    });
  });

  const values = flatRows.map((row) =>
    headers.map((header) => {
      const value = row.columns?.[header];
      return value === null || value === undefined ? "" : value;
    })
  );

  return { headers, values };
}
